"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { NAKSHATRA_DATA, type NakshatraData } from "@/lib/nakshatra-page-data";
import { publicNakshatraSlug } from "@/lib/nakshatra-route-metadata";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";

const RULER_FILTERS = [
  "all",
  "ketu",
  "venus",
  "sun",
  "moon",
  "mars",
  "rahu",
  "jupiter",
  "saturn",
  "mercury",
] as const;

export default function NakshatraStudio() {
  const [selectedId, setSelectedId] = useState<string>("ashwini");
  const [activeFilter, setActiveFilter] = useState<string>("all");

  const filteredNakshatras = useMemo(() => {
    if (activeFilter === "all") return NAKSHATRA_DATA;
    return NAKSHATRA_DATA.filter((n) => n.ruler.toLowerCase().includes(activeFilter));
  }, [activeFilter]);

  const nakshatra = useMemo<NakshatraData>(() => {
    return NAKSHATRA_DATA.find((n) => n.id === selectedId) || NAKSHATRA_DATA[0];
  }, [selectedId]);

  const handleConsult = () => {
    const prompt = `What are the complete Vedic characteristics, career strengths, marriage compatibility, and remedies for ${nakshatra.name_en} (${nakshatra.name_hi}) Nakshatra in a birth chart?`;
    try {
      safeSessionSet("seo_entry_intent", "chart_report");
      safeSessionSet("seo_entry_path", "/nakshatra");
      safeSessionSet("seo_entry_prompt", prompt);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // Storage fallback
    }
  };

  return (
    <div className="rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-8 shadow-sm">
      <StudioHeader />
      <FilterBar activeFilter={activeFilter} onFilterChange={setActiveFilter} />
      <NakshatraSelector
        nakshatras={filteredNakshatras}
        selectedId={selectedId}
        onSelect={setSelectedId}
      />
      <NakshatraDetailCard nakshatra={nakshatra} onConsult={handleConsult} />
      <PadaBreakdownCard nakshatra={nakshatra} />
    </div>
  );
}

function StudioHeader() {
  return (
    <div className="text-center max-w-2xl mx-auto mb-6">
      <span className="text-eyebrow font-bold uppercase tracking-wider text-intent-brand">
        Interactive Lunar Mansion Explorer
      </span>
      <h2 className="text-title font-bold text-text-primary mt-1 font-serif">
        27 Nakshatras &amp; Pada Diagnostic Studio
      </h2>
      <p className="text-body-small text-text-secondary mt-1">
        Select any Nakshatra to explore its ruling deity, planetary lord, 4 Padas, Navamsha
        divisions, and core life traits.
      </p>
    </div>
  );
}

function FilterBar({
  activeFilter,
  onFilterChange,
}: {
  activeFilter: string;
  onFilterChange: (f: string) => void;
}) {
  return (
    <div className="overflow-x-auto pb-2 mb-4">
      <div className="flex gap-1.5 min-w-[600px]">
        {RULER_FILTERS.map((ruler) => {
          const isSelected = activeFilter === ruler;
          return (
            <button
              key={ruler}
              type="button"
              onClick={() => onFilterChange(ruler)}
              className={`px-3 py-1.5 rounded-lg text-caption font-bold capitalize transition-all ${
                isSelected
                  ? "bg-intent-brand text-text-on-brand shadow-sm"
                  : "bg-surface-raised border border-border-default text-text-secondary hover:bg-surface-warm"
              }`}
            >
              {ruler === "all" ? "All 27 Stars" : `${ruler} Ruled`}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NakshatraSelector({
  nakshatras,
  selectedId,
  onSelect,
}: {
  nakshatras: NakshatraData[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="mb-6">
      <label
        htmlFor="nakshatra-select"
        className="block text-caption font-bold uppercase tracking-wider text-text-muted mb-2"
      >
        Select Nakshatra
      </label>
      <div className="relative">
        <select
          id="nakshatra-select"
          value={selectedId}
          onChange={(e) => onSelect(e.target.value)}
          className="w-full appearance-none rounded-xl border border-border-default bg-surface-raised px-4 py-3 text-body-small font-semibold text-text-primary focus:border-border-interactive focus:outline-none focus:ring-2 focus:ring-intent-brand pr-10"
        >
          {nakshatras.map((n) => (
            <option key={n.id} value={n.id}>
              #{n.number} {n.name_en} ({n.name_hi}) — Ruled by {n.ruler} ({n.rashi_en})
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-text-muted text-caption">
          ▼
        </span>
      </div>
    </div>
  );
}

function NakshatraDetailCard({
  nakshatra,
  onConsult,
}: {
  nakshatra: NakshatraData;
  onConsult: () => void;
}) {
  const chatHref = buildSEOAgentHref(getSEOIntentConfigForSource("/nakshatra"), "/nakshatra", "en");
  const slug = publicNakshatraSlug(nakshatra.id);

  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-6 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-border-default">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold font-serif text-text-primary">
              #{nakshatra.number} {nakshatra.name_en} ({nakshatra.name_hi})
            </h3>
            <span className="px-3 py-0.5 rounded-full text-caption font-bold bg-surface-brand-subtle text-intent-brand">
              {nakshatra.ruler} Ruled
            </span>
          </div>
          <p className="text-caption text-text-secondary mt-1">
            Zodiac Span:{" "}
            <strong className="text-text-primary">
              {nakshatra.degree_start}° to {nakshatra.degree_end}° ({nakshatra.rashi_en})
            </strong>
          </p>
        </div>

        <a
          href={chatHref}
          onClick={onConsult}
          className="shrink-0 inline-flex items-center justify-center rounded-lg bg-surface-brand-strong px-5 py-2.5 text-button font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shadow-sm"
        >
          Consult on {nakshatra.name_en} ✦
        </a>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5">
        <FactBlock label="Presiding Deity" value={nakshatra.deity} />
        <FactBlock label="Sacred Symbol" value={nakshatra.symbol} />
        <FactBlock label="Spiritual Gana" value={nakshatra.gana} />
        <FactBlock label="Nakshatra Nature" value={nakshatra.nature} />
      </div>

      <div className="mt-5 pt-4 border-t border-border-default flex flex-col sm:flex-row items-center justify-between gap-3 text-caption">
        <span className="text-text-secondary">
          Key themes: {nakshatra.signifies.slice(0, 3).join(", ")}
        </span>
        <Link
          href={`/nakshatra/${slug}/`}
          className="font-semibold text-intent-brand hover:underline flex items-center gap-1"
        >
          Read Full {nakshatra.name_en} Guide →
        </Link>
      </div>
    </div>
  );
}

function FactBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 rounded-lg bg-surface-raised border border-border-default">
      <span className="block text-eyebrow font-bold uppercase tracking-wider text-text-muted">
        {label}
      </span>
      <span className="block text-body-small font-bold text-text-primary mt-0.5 capitalize">
        {value}
      </span>
    </div>
  );
}

/**
 * `degree_start`/`degree_end` are on NAKSHATRA_DATA and were not shown anywhere.
 * A pada is a fixed quarter of the nakshatra's 13°20', so its sidereal bounds
 * are arithmetic on the nakshatra's own start — no interpretation, and the one
 * fact that makes a pada locatable on the zodiac rather than just described.
 */
function padaSpan(nakshatra: NakshatraData, padaIndex: number): string {
  const padaWidth = (nakshatra.degree_end - nakshatra.degree_start) / 4;
  const start = nakshatra.degree_start + padaIndex * padaWidth;
  return `${formatDegrees(start)} – ${formatDegrees(start + padaWidth)}`;
}

function formatDegrees(value: number): string {
  const whole = Math.floor(value);
  const minutes = Math.round((value - whole) * 60);
  // 13.333° rounds to 13°20'; carrying 60' avoids rendering "12°60'".
  return minutes === 60 ? `${whole + 1}°00′` : `${whole}°${String(minutes).padStart(2, "0")}′`;
}

function PadaBreakdownCard({ nakshatra }: { nakshatra: NakshatraData }) {
  return (
    <div>
      <h4 className="text-body-small font-bold uppercase tracking-wider text-text-primary mb-3">
        4 Padas (Quarters) &amp; Navamsha Breakdown
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {nakshatra.padas.map((pada, idx) => (
          <div key={idx} className="p-4 rounded-xl border border-border-default bg-surface-raised">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-body-small font-bold text-text-primary">Pada {idx + 1}</span>
              <span className="text-caption font-semibold text-intent-brand">
                {pada.navamsha} Navamsha
              </span>
            </div>
            <p className="text-caption font-medium text-text-muted mb-1">
              {padaSpan(nakshatra, idx)} sidereal
            </p>
            <p className="text-caption text-text-secondary leading-relaxed">{pada.traits}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
